const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let backdrop = null;
let activeSheet = null;
let lastFocused = null;

function createBackdrop() {
    if (backdrop) return backdrop;

    backdrop = document.createElement('div');
    backdrop.className = 'hm-sheet-backdrop';
    backdrop.hidden = true;
    backdrop.addEventListener('click', () => close(activeSheet));
    document.body.appendChild(backdrop);

    return backdrop;
}

function resolve(target) {
    if (!target) return null;
    if (typeof target === 'string') return document.getElementById(target);
    return target;
}

function open(target) {
    const sheet = resolve(target);
    if (!sheet || sheet === activeSheet) return;
    if (activeSheet) close(activeSheet, false);

    const bd = createBackdrop();
    lastFocused = document.activeElement;
    activeSheet = sheet;

    bd.hidden = false;
    sheet.hidden = false;
    window.HMScrollLock?.lock(sheet);
    document.body.classList.add('hm-sheet-locked');

    void sheet.offsetHeight;

    bd.classList.add('is-open');
    sheet.classList.add('is-open');

    sheet.setAttribute('aria-hidden', 'false');
    const first = sheet.querySelector(FOCUSABLE);
    if (first) first.focus({ preventScroll: true });

    sheet.dispatchEvent(new CustomEvent('hm-sheet:open', { bubbles: true }));
}

function close(target, restoreFocus = true) {
    const sheet = resolve(target) || activeSheet;
    if (!sheet) return;

    sheet.classList.remove('is-open');
    sheet.setAttribute('aria-hidden', 'true');

    if (backdrop) backdrop.classList.remove('is-open');

    const finish = () => {
        sheet.hidden = true;
        if (backdrop && !activeSheet) backdrop.hidden = true;
        sheet.removeEventListener('transitionend', finish);
    };

    const duration = parseFloat(getComputedStyle(sheet).transitionDuration) || 0;
    if (duration > 0) {
        sheet.addEventListener('transitionend', finish);
        setTimeout(finish, duration * 1000 + 80);
    } else {
        finish();
    }

    if (sheet === activeSheet) activeSheet = null;
    window.HMScrollLock?.unlock(sheet);
    if (!activeSheet) document.body.classList.remove('hm-sheet-locked');

    if (restoreFocus && lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
        lastFocused = null;
    }

    sheet.dispatchEvent(new CustomEvent('hm-sheet:close', { bubbles: true }));
}

function bindDrag(sheet) {
    const handle = sheet.querySelector('.hm-sheet-handle');
    if (!handle) return;

    let startY = 0;
    let distance = 0;
    let dragging = false;

    const start = (e) => {
        dragging = true;
        distance = 0;
        startY = e.touches ? e.touches[0].clientY : e.clientY;
        sheet.style.transition = 'none';
    };

    const move = (e) => {
        if (!dragging) return;
        const currentY = e.touches ? e.touches[0].clientY : e.clientY;
        distance = Math.max(0, currentY - startY);
        sheet.style.transform = `translateY(${distance}px)`;
    };

    const finish = () => {
        if (!dragging) return;
        dragging = false;
        sheet.style.transition = '';
        sheet.style.transform = '';
        if (distance > sheet.offsetHeight * 0.25 || distance > 120) close(sheet);
    };

    handle.addEventListener('touchstart', start, { passive: true });
    handle.addEventListener('touchmove', move, { passive: true });
    handle.addEventListener('touchend', finish);
    handle.addEventListener('mousedown', start);
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', finish);
}

document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-sheet-open]');
    if (opener) {
        e.preventDefault();
        open(opener.dataset.sheetOpen);
        return;
    }

    const closer = e.target.closest('[data-sheet-close], [data-hm-mobile-menu-close]');
    if (closer && closer.closest('.hm-sheet')) {
        e.preventDefault();
        close(closer.closest('.hm-sheet'));
    }
});

document.addEventListener('keydown', (e) => {
    if (!activeSheet) return;

    if (e.key === 'Escape') {
        close(activeSheet);
        return;
    }

    if (e.key !== 'Tab') return;

    const items = [...activeSheet.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
    }
});

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.hm-sheet--dismissible').forEach(bindDrag);
});

window.HmSheet = { open, close };

export { open, close };
